import { useState } from "react"
import axios from "axios"
import { toast } from "react-toastify"
import { PDFDocument } from "pdf-lib"
import { API_ESPACE_FOURNISSEUR_EXTRACT_HT_TVA_WORKING_DAYS_CONSULTANT_NAME_FROM_INVOICE_URL_ENDPOINT, API_URL_ESPACE_CONSULTANT } from "../../../../constants"

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ExtractionResult {
     prestationHt: number
     prestationVat: number
     fraisHt: number
     fraisVat: number
     nbJoursTravailles: number
}

export type SaisieStep = "upload" | "review"

export interface SaisieState {
     step: SaisieStep
     factureFile: File | null
     fraisFiles: File[]
     extracted: ExtractionResult | null
     isExtracting: boolean
     isSaving: boolean
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useSaisieDeclarationST = (
     consultantId: number,
     mois: string,
     annee: string,
     onSuccess: () => void
) => {
     const [step, setStep] = useState<SaisieStep>("upload")
     const [factureFile, setFactureFile] = useState<File | null>(null)
     const [fraisFiles, setFraisFiles] = useState<File[]>([])
     const [extracted, setExtracted] = useState<ExtractionResult | null>(null)
     const [isExtracting, setIsExtracting] = useState(false)
     const [isSaving, setIsSaving] = useState(false)

     // ── Extraction ─────────────────────────────────────────────────────────────

     const handleFactureDrop = async (files: FileList | null) => {
          if (!files || files.length === 0) return
          const file = files[0]
          setFactureFile(file)
          setIsExtracting(true)

          try {
               let fileToSend = file
               if (file.type === "application/pdf") {
                    const arrayBuffer = await file.arrayBuffer()
                    const pdfDoc = await PDFDocument.load(arrayBuffer)
                    if (pdfDoc.getPageCount() > 1) {
                         const subDoc = await PDFDocument.create()
                         const [firstPage] = await subDoc.copyPages(pdfDoc, [0])
                         subDoc.addPage(firstPage)
                         const pdfBytes = await subDoc.save()
                         fileToSend = new File([pdfBytes], file.name, { type: "application/pdf" })
                    }
               }

               const formData = new FormData()
               formData.append("files[0]", fileToSend)
               formData.append("consultantIds[0]", consultantId.toString())
               formData.append("mois", `${annee}-${mois}`)

               const { data } = await axios.post<{ declarations: any[] }>(API_ESPACE_FOURNISSEUR_EXTRACT_HT_TVA_WORKING_DAYS_CONSULTANT_NAME_FROM_INVOICE_URL_ENDPOINT, formData)

               const fileGroups: any[] = data.declarations || []
               const result: ExtractionResult = { prestationHt: 0, prestationVat: 0, fraisHt: 0, fraisVat: 0, nbJoursTravailles: 0 }

               fileGroups.forEach(fileGroup => {
                    Object.keys(fileGroup).forEach(key => {
                         if (key === "fileName") return
                         const line = fileGroup[key]
                         result.prestationHt += Number(line.prestationHt) || 0
                         result.prestationVat += Number(line.prestationVat) || 0
                         result.fraisHt += Number(line.fraisHt) || 0
                         result.fraisVat += Number(line.fraisVat) || 0
                         result.nbJoursTravailles += Number(line.nbJoursTravailles) || 0
                    })
               })

               setExtracted(result)
               setStep("review")
          } catch (e: any) {
               toast.error("Échec de l'extraction. Veuillez réessayer.", { autoClose: false })
          } finally {
               setIsExtracting(false)
          }
     }

     // ── Field update ───────────────────────────────────────────────────────────

     const updateField = (field: keyof ExtractionResult, value: number) => {
          setExtracted(prev => (prev ? { ...prev, [field]: value } : prev))
     }

     // ── Frais files ────────────────────────────────────────────────────────────

     const addFraisFiles = (files: FileList | null) => {
          if (!files) return
          setFraisFiles(prev => [...prev, ...Array.from(files)])
     }

     const removeFraisFile = (index: number) => {
          setFraisFiles(prev => prev.filter((_, i) => i !== index))
     }

     // ── Submission ─────────────────────────────────────────────────────────────

     const handleSubmit = async () => {
          if (!extracted || !factureFile) return

          if (extracted.fraisHt > 0 && fraisFiles.length === 0) {
               toast.warning("Une facture de frais est obligatoire lorsque le montant des frais HT est supérieur à 0.", { autoClose: false })
               return
          }

          setIsSaving(true)
          const formData = new FormData()
          formData.append("mois", mois)
          formData.append("annee", annee)
          formData.append("declarations[0][consultantId]", consultantId.toString())
          formData.append("declarations[0][nbJoursTravailles]", extracted.nbJoursTravailles.toString())
          formData.append("declarations[0][prestationHt]", extracted.prestationHt.toString())
          formData.append("declarations[0][prestationVat]", extracted.prestationVat.toString())
          formData.append("declarations[0][fraisHt]", extracted.fraisHt.toString())
          formData.append("declarations[0][fraisVat]", extracted.fraisVat.toString())
          formData.append("declarations[0][facture]", factureFile)
          fraisFiles.forEach(f => formData.append("declarations[0][justificatifs][]", f))

          try {
               const { data } = await axios.post<{ success: number; errors: string[] }>(`${API_URL_ESPACE_CONSULTANT}/declaration/add-facture-presta-st-on-mass`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
               })

               if (data.errors?.length > 0) {
                    data.errors.forEach(err => toast.error(err, { autoClose: false }))
                    setIsSaving(false)
               } else {
                    toast.success("La déclaration a bien été envoyée.")
                    onSuccess()
               }
          } catch (e: any) {
               toast.error("Erreur lors de l'envoi.", { autoClose: false })
               setIsSaving(false)
          }
     }

     // ── Reset ──────────────────────────────────────────────────────────────────

     const reset = () => {
          setStep("upload")
          setFactureFile(null)
          setFraisFiles([])
          setExtracted(null)
     }

     return {
          state: { step, factureFile, fraisFiles, extracted, isExtracting, isSaving },
          handlers: { handleFactureDrop, updateField, addFraisFiles, removeFraisFile, handleSubmit, reset },
     }
}
